import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { getRouteApi } from '@tanstack/react-router';
import { cn } from 'cn';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueryAnalyticsChart } from '~/data/analytics';

import { getFormattedNumber, toCamelCase } from '@vestigas/utils';
import { i18n } from '~/utils';

import { HeadingRow, Icon, Tooltip, withErrorBoundary } from '@vestigas/ui';

import {
  CHART_SINGLE_DATASET_COLOR,
  DELIVERY_NOTES_CHART_TYPE,
} from '../constants';
import { formatDateForChartTooltip, useDateRange } from '../utils';

import { ChartLegend, getLegendData } from './ChartLegend';
import { DataCard } from './DataCard';
import { TimeSeriesChart } from './TimeSeriesChart';

export type DeliveryNotesChartType = (typeof DELIVERY_NOTES_CHART_TYPE)[number];

const CardContent = withErrorBoundary(
  () => {
    const { t } = useTranslation('analytics');

    const AnalyticsUsageRoute = getRouteApi('/(main)/analytics/usage/');

    const navigate = AnalyticsUsageRoute.useNavigate();

    const dateRange = useDateRange();

    const deliveryNotesChartType = AnalyticsUsageRoute.useSearch({
      select: ({ deliveryNotesChartType }) => deliveryNotesChartType,
    });

    const labelTranslationsMap = {
      assigned: t(($) => $.usage.datasetLabel.assignedDeliveryNotes),
      signed: t(($) => $.usage.datasetLabel.deliveryNotesSignatureRate), //
      unsigned: t(($) => $.usage.datasetLabel.unsignedDeliveryNotes),
    };

    const deliveryNotesChart = useQueryAnalyticsChart({
      dateRange,
      name: 'weekly_dlns',
    });

    const chartConfig = useMemo(() => {
      // Default empty data structure to prevent plugin errors
      const emptyData = { datasets: [], labels: [] };

      if (deliveryNotesChartType === 'absolute') {
        // Merge all datasets into one by summing values for absolute mode
        const mergedData = (() => {
          if (
            !deliveryNotesChart.data?.datasets ||
            deliveryNotesChart.data.datasets.length === 0
          ) {
            return deliveryNotesChart.data ?? emptyData;
          }

          const { datasets, labels = [] } = deliveryNotesChart.data;
          const dataLength = datasets[0]?.data?.length ?? 0;
          const mergedDataPoints: Array<{ x: any; y: number }> = [];

          for (let i = 0; i < dataLength; i++) {
            let sum = 0;
            let xValue = null;

            for (const dataset of datasets) {
              const dataPoint = dataset.data[i];
              if (
                typeof dataPoint === 'object' &&
                dataPoint !== null &&
                'y' in dataPoint
              ) {
                sum += (dataPoint.y as number) || 0;
                if (xValue === null) {
                  xValue = dataPoint.x;
                }
              } else {
                sum += (dataPoint as number) || 0;
              }
            }

            mergedDataPoints.push({ x: xValue, y: sum });
          }

          return {
            datasets: [
              {
                backgroundColor: CHART_SINGLE_DATASET_COLOR,
                data: mergedDataPoints,
                label: 'assigned',
              },
            ],
            labels,
          };
        })();

        return {
          data: mergedData,
          options: {
            plugins: {
              stacked100: { enable: false },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    return t(($) => $.usage.deliveryNotesChart.tooltipLabel, {
                      value: getFormattedNumber({
                        value: context.parsed.y,
                        digits: 0,
                      }),
                      dataset:
                        labelTranslationsMap?.[
                          toCamelCase(context.dataset.label ?? '')
                        ] || context.dataset.label,
                      date: formatDateForChartTooltip(context.raw.x),
                    });
                  },
                  title: () => null,
                },
              },
            },
            scales: {
              x: {
                stacked: false,
              },
              y: {
                stacked: false,
                suggestedMax: 40,
                ticks: {
                  callback: (value: number) =>
                    getFormattedNumber({
                      value,
                      digits: 0,
                    }),
                },
              },
            },
          },
        };
      }

      /**
       * Options for relative (percentage) mode with stacked100 plugin
       * - Fake the existence of a "signature rate" dataset:
       *   - scale "signed" and "unsigned" datasets to 100% so that "signed" bar is displayed like a percentage
       *   - re-label the "signed" dataset as "signature rate"
       * - Disable hover effect and tooltip for 'unsigned' dataset => make "unsigned" columns appear like filler bars
       */
      const dataWithDisabledHover = {
        labels: deliveryNotesChart.data?.labels ?? [],
        datasets:
          deliveryNotesChart.data?.datasets?.map((dataset) =>
            dataset.label === 'unsigned'
              ? { ...dataset, hoverBackgroundColor: dataset.backgroundColor }
              : dataset,
          ) ?? [],
      };

      return {
        data: dataWithDisabledHover,
        options: {
          plugins: {
            stacked100: {
              enable: true,
              replaceTooltipLabel: false, // We handle tooltips ourselves
            },
            tooltip: {
              filter: (tooltipItem) => {
                // Exclude 'unsigned' dataset from tooltip
                return tooltipItem?.dataset?.label !== 'unsigned';
              },
              callbacks: {
                label: (context) => {
                  return t(
                    ($) => $.usage.deliveryNotesChart.tooltipLabelRelative,
                    {
                      value: getFormattedNumber({
                        value: context.parsed.y,
                        digits: 0,
                      }),
                      dataset:
                        labelTranslationsMap?.[
                          toCamelCase(context.dataset.label ?? '')
                        ] || context.dataset.label,
                      date: formatDateForChartTooltip(context.raw.x),
                    },
                  );
                },
                title: () => null,
              },
            },
          },
          scales: {
            x: {
              stacked: true,
            },
            y: {
              stacked: true,
              suggestedMax: 100,
              ticks: {
                callback: (value: number) =>
                  `${getFormattedNumber({
                    value,
                    digits: 0,
                  })} %`,
                stepSize: 20,
              },
            },
          },
        },
      };
    }, [
      deliveryNotesChart.data,
      deliveryNotesChartType,
      labelTranslationsMap,
      t,
    ]);

    function onToggleDeliveryNotesChartType(
      deliveryNotesChartType: DeliveryNotesChartType,
    ) {
      navigate({
        search: (previous) => ({
          ...previous,
          deliveryNotesChartType,
        }),
      });
    }

    return (
      <>
        <DataCard.Header>
          <HeadingRow
            heading={
              <div className="flex items-center">
                {deliveryNotesChartType === 'absolute'
                  ? t(($) => $.usage.deliveryNotesChart.headingAbsolute)
                  : t(($) => $.usage.deliveryNotesChart.headingRelative)}
                <Tooltip
                  content={
                    deliveryNotesChartType === 'absolute'
                      ? t(($) => $.usage.deliveryNotesChart.subHeadingAbsolute)
                      : t(($) => $.usage.deliveryNotesChart.subHeadingRelative)
                  }
                >
                  <span className="inline-block -translate-y-1 pl-1">
                    <Icon
                      className="cursor-help text-tiga-action-default transition-colors duration-300 hover:text-tiga-action-lighter"
                      metaphor="infoOutlined"
                      size="sm"
                    />
                  </span>
                </Tooltip>
              </div>
            }
            action={
              <ToggleButtonGroup
                className="-my-0.5 h-8"
                value={deliveryNotesChartType}
                onChange={(_, value: DeliveryNotesChartType | null) => {
                  if (value !== null) {
                    onToggleDeliveryNotesChartType(value);
                  }
                }}
                exclusive
              >
                <ToggleButton value="relative" className="min-w-16 flex-1">
                  {t(($) => $.usage.deliveryNotesChart.relativeButton.text)}
                </ToggleButton>
                <ToggleButton value="absolute" className="min-w-16 flex-1">
                  {t(($) => $.usage.deliveryNotesChart.absoluteButton.text)}
                </ToggleButton>
              </ToggleButtonGroup>
            }
            variant="h3"
          />
        </DataCard.Header>
        <DataCard.Body
          className="py-0"
          isLoading={deliveryNotesChart.isFetching}
        >
          <TimeSeriesChart
            aggregationInterval="month"
            data={chartConfig.data}
            options={chartConfig.options}
            showDataLabels={deliveryNotesChartType === 'absolute'}
          />
        </DataCard.Body>
        <DataCard.Footer className="pt-2">
          <ChartLegend
            data={getLegendData(
              {
                ...chartConfig.data,
                datasets:
                  chartConfig.data?.datasets?.filter(
                    (dataset) => dataset.label !== 'unsigned', // remove 'unsigned' dataset since "signed" is displayed as "signature rate" (see above)
                  ) ?? [],
              },
              labelTranslationsMap,
            )}
          />
        </DataCard.Footer>
      </>
    );
  },
  i18n.t(($) => $.errorBoundary.fallbackMessage, {
    ns: 'analytics',
    section: i18n.t(($) => $.usage.deliveryNotes.heading, { ns: 'analytics' }),
  }),
  false,
  'row-span-3',
);

CardContent.displayName = 'DeliveryNoteActivityChartContent';

/**
 * DeliveryNoteActivityChart
 *
 * Displays a time series chart visualizing delivery note activity (assigned, signed, unsigned delivery notes) for the selected date range.
 * Allows toggling between displaying absolute and relative values.
 */
export const DeliveryNoteActivityChart = ({
  className,
  style,
}: ComponentStyling) => {
  return (
    <DataCard
      className={cn('bg-tiga-surface-off-white', className)}
      style={style}
    >
      <CardContent />
    </DataCard>
  );
};
